import { GD } from "src/GD";
import { attachPhoneTakenField, CARD_HOLDER_PHONE_TAKEN_ERROR_CODE } from "src/utils/errors";
const CARDHOLDER_ADDRESS_FIELDS = ["address_line1", "address_line2", "city_code", "state", "postcode", "country_code"];
// Server-managed / never-editable fields that must not be sent on update.
const CARDHOLDER_IMMUTABLE_FIELDS = ["uid", "provider", "provider_id", "providerData", "company_uid", "user_uid", "holder_id", "created_at", "status", "status_message", "type"];
type DynamicRecord = Record<string, unknown>;

const asDynamicRecord = (value: unknown): DynamicRecord | null =>
	value !== null && typeof value === "object" ? value as DynamicRecord : null;

const formValuesEqual = (left: unknown, right: unknown): boolean => {
	if (left === right) return true;
	if ((left === null || left === undefined) && (right === null || right === undefined)) return true;
	const leftIsScalar = ["string", "number", "boolean"].includes(typeof left);
	const rightIsScalar = ["string", "number", "boolean"].includes(typeof right);
	return leftIsScalar && rightIsScalar && String(left) === String(right);
};

interface ICardHolderFormEnvelope {
	holder: ICardHolderResponse;
	missing_fields?: string[];
	readonly_fields?: string[];
	hidden_fields?: string[];
	provider_code?: string;
}

export class CardHoldersService {
	// Snapshot of each cardholder as last fetched, used to send only the delta on update.
	private _snapshots: Record<string, DynamicRecord> = {};

	constructor() {
		GD.REQ_CARDHOLDER_CREATE.listener = this.create;
		GD.REQ_CARDHOLDER_TEMPLATE.listener = this.getTemplate;
		GD.REQ_CARDHOLDERS.listener = this.getAll;
		GD.REQ_CARDHOLDER_GET.listener = this.get;
		GD.REQ_CARDHOLDER_UPDATE.listener = this.update;
		GD.REQ_CARDHOLDER_COMPLETION_GET.listener = this.getCompletionModel;
		GD.REQ_CARDHOLDER_COMPLETION_UPDATE.listener = this.updateCompletionModel;
		GD.REQ_CARDHOLDER_FORM.listener = this.getPrivateForm;
		GD.REQ_CARDHOLDER_SUBMIT.listener = this.submit;
	}

	private decorateCompletionModel = (data: ICardHolderResponse) => {
		data.$__uid_hidden = true;
		data.$__company_uid_hidden = true;
		data.$__provider_hidden = true;
		data.$__provider_id_hidden = true;
		data.$__providerData_hidden = true;
		data.$__holder_id_hidden = true;
		data.$__created_at_hidden = true;
		data.$__status_hidden = true;
		data.$__status_message_hidden = true;

		if (data.area_code === undefined || data.area_code === null) data.area_code = "";
		if (!data.$__birthday_type) data.$__birthday_type = "birthday";

		// Keep the nested model returned by cardholders/get intact. Its metadata is
		// authoritative, including the issuer-provided global city catalog.
		data.address ??= {
			address_line1: "",
			address_line2: "",
			city_code: "",
			state: "",
			postcode: "",
			country_code: "",
		};
		data.address.city_code ??= "";
		data.address.country_code ??= "";
		data.address.$__city_code_type ??= "select";
		data.address.$__city_code_values ??= [];
		// Some issuer forms wrap City in an object; keep its input but skip the extra card frame.
		data.address.$__city_code_blockless = true;

		data.$__decorator = {
			blocks: [
				{
					title: "Personal Information",
					order: ["first_name", "last_name", "email", "area_code", "mobile", "birthday", "gender"],
					gridTemplateColumns: "1fr 1fr",
					sx: { alignItems: "start", "@media (max-width: 620px)": { gridTemplateColumns: "minmax(0, 1fr)" } },
				},
				{
					title: "Identification",
					order: ["id_type", "id_no", "nationality_code"],
					gridTemplateColumns: "1fr 1fr",
					sx: { alignItems: "start", "@media (max-width: 620px)": { gridTemplateColumns: "minmax(0, 1fr)" } },
				},
				{ title: "Address", order: ["address"] },
			],
		};
		data.$__address_decorator = {
			title: "",
			blockless: true,
			gridTemplateColumns: "1fr 1fr",
			sx: { alignItems: "start", "@media (max-width: 620px)": { gridTemplateColumns: "minmax(0, 1fr)" } },
		};

		return data;
	};

	private applyReadonlyFields = (holder: ICardHolderResponse, fields: string[]) => {
		this.applyFieldFlags(holder, fields, "disabled");
	};

	private applyHiddenFields = (holder: ICardHolderResponse, fields: string[]) => {
		this.applyFieldFlags(holder, fields, "hidden");
	};

	private applyFieldFlags = (
		holder: ICardHolderResponse,
		fields: string[],
		flag: "disabled" | "hidden"
	) => {
		for (const path of fields) {
			const parts = path.split(".");
			const field = parts.pop();
			if (!field) continue;

			let target = holder as unknown as DynamicRecord;
			let pathExists = true;
			for (const part of parts) {
				const nested = asDynamicRecord(target[part]);
				if (!nested) {
					pathExists = false;
					break;
				}
				target = nested;
			}
			if (pathExists) target[`$__${field}_${flag}`] = true;
		}
	};

	private normalizeFormModel = async (raw: unknown): Promise<ICardHolderResponse> => {
		const value = raw as ICardHolderResponse | ICardHolderFormEnvelope;
		const envelope =
			value && typeof value === "object" && "holder" in value
				? (value as ICardHolderFormEnvelope)
				: null;
		const holder = envelope?.holder ?? (value as ICardHolderResponse);
		const missingFields = envelope?.missing_fields ?? holder.missing_fields ?? [];
		const readonlyFields = envelope?.readonly_fields ?? holder.readonly_fields ?? [];
		const hiddenFields = envelope?.hidden_fields ?? holder.hidden_fields ?? [];
		const providerCode = envelope?.provider_code ?? holder.provider_code;

		holder.missing_fields = missingFields;
		holder.$__missing_fields_hidden = true;
		holder.readonly_fields = readonlyFields;
		holder.$__readonly_fields_hidden = true;
		holder.hidden_fields = hiddenFields;
		holder.$__hidden_fields_hidden = true;
		holder.$__issuer_hidden = true;
		if (providerCode) {
			holder.provider_code = providerCode;
			holder.$__provider_code_hidden = true;
		}
		this.applyReadonlyFields(holder, readonlyFields);
		this.applyHiddenFields(holder, hiddenFields);

		return this.decorateCompletionModel(holder);
	};

	private getCompletionModel: (
		context: { uid: string; issuer?: number }
	) => Promise<IAPIResponse<ICardHolderResponse>> = async context => {
		if (!context?.uid) return { error: { code: 1, message: "Card holder UID is required" } };

		const res = await GD.REQ_API.request({
			httpMethod: "POST",
			method: "cardholders/form",
			data: { issuer: context.issuer },
		});
		if (res.error) return res as IAPIResponse<ICardHolderResponse>;

		return { data: await this.normalizeFormModel(res.data) };
	};

	private updateCompletionModel: (
		data: ICardHolderResponse
	) => Promise<IAPIResponse<IStatusResponse>> = async data => {
		if (!data?.uid) return { error: { code: 1, message: "Card holder UID is required" } };
		const payload = { ...data };
		delete payload.missing_fields;
		delete payload.readonly_fields;
		delete payload.hidden_fields;
		delete payload.provider_code;

		const res = (await GD.REQ_API.request({
			httpMethod: "POST",
			method: "cardholders/update",
			// HTTP removes $__ metadata recursively; all actual model fields remain.
			data: payload,
		})) as IAPIResponse<IStatusResponse>;
		return res.error ? { error: attachPhoneTakenField(res.error, "mobile") } : res;
	};

	private getPrivateForm: (context: void | { issuer?: number; bin?: string }) => Promise<IAPIResponse<ICardHolderResponse>> = async context => {
		const res = await GD.REQ_API.request({ httpMethod: "POST", method: "cardholders/form", data: { issuer: context && context.issuer, bin: (context && context.bin) || undefined } });
		if (res.error) return res as IAPIResponse<ICardHolderResponse>;
		return { data: await this.normalizeFormModel(res.data) };
	};

	private submit: (context: void | { issuer?: number; bin?: string }) => Promise<IAPIResponse<ICardHolderSubmitResponse>> = async context =>
		(await GD.REQ_API.request({ httpMethod: "POST", method: "cardholders/submit", data: { issuer: context && context.issuer, bin: (context && context.bin) || undefined } })) as IAPIResponse<ICardHolderSubmitResponse>;

	private decorate = (data: ICardHolderResponse) => {
		if (data.address && typeof data.address === "object") {
			const address = data.address;
			const decorated = data as unknown as DynamicRecord;

			for (const key in address) {
				decorated[key] = address[key];
			}
			delete decorated.address;
		}

		// Hidden fields
		data.$__uid_hidden = true;
		data.$__company_uid_hidden = true;
		data.$__holder_id_hidden = true;
		data.$__created_at_hidden = true;
		data.$__provider_hidden = true;
		data.$__provider_id_hidden = true;
		data.$__status_hidden = true;
		data.$__status_message_hidden = true;
		this.applyHiddenFields(data, data.hidden_fields ?? []);

		// Values
		data.$__gender_values ??= [
			{ value: "0", name: "---" },
			{ value: "1", name: "Male" },
			{ value: "2", name: "Female" },
		];
		data.$__id_type_values ??= [
			{ value: "0", name: "---" },
			{ value: "1", name: "Europe ID card" },
			{ value: "2", name: "Passport" },
		];

		data.$__gender_type ??= "select";
		data.$__id_type_type ??= "select";

		// The backend provides the complete dial-code select. Preserve its metadata
		// and only supply harmless fallbacks for older responses.
		if (data.area_code === undefined || data.area_code === null) data.area_code = "";
		data.$__area_code_type ??= "select";
		data.$__area_code_values ??= [];
		data.$__area_code_label ??= "Phone code (+1, +44…)";

		// Birthday is yyyy-MM-dd (required for wasabi) — render as a masked text picker
		// unless the backend already specified a field type.
		if (!data.$__birthday_type) data.$__birthday_type = "birthday";

		// Country: honour a backend-provided ISO-2 select; otherwise fall back to
		// the built-in country picker.
		if (data.$__country_code_type !== "select") data.$__country_code_type = "country";

		// City is an issuer-code select with a global catalog. It is independent of
		// country_code, so never replace, filter, or reset the server-provided list.
		data.$__city_code_type ??= "select";
		data.$__city_code_values ??= [];

		// Layout
		data.$__decorator = {
			blocks: [
				{
					title: "Personal Information",
					order: ["first_name", "last_name", "email", "area_code", "mobile", "birthday", "gender"],
					gridTemplateColumns: "1fr 1fr",
					sx: { alignItems: "start", "@media (max-width: 620px)": { gridTemplateColumns: "minmax(0, 1fr)" } },
				},
				{
					title: "Identification",
					order: ["id_type", "id_no", "nationality_code"],
					gridTemplateColumns: "1fr 1fr",
					sx: { alignItems: "start", "@media (max-width: 620px)": { gridTemplateColumns: "minmax(0, 1fr)" } },
				},
				{
					title: "Address",
					order: ["country_code", "state", "city_code", "postcode", "address_line1", "address_line2"],
					gridTemplateColumns: "1fr 1fr",
					sx: { alignItems: "start", "@media (max-width: 620px)": { gridTemplateColumns: "minmax(0, 1fr)" } },
				},
			],
		};

		return data as unknown as ICardHolderRaw;
	};

	private getTemplate: () => Promise<IAPIResponse<ICardHolderRaw>> = async () => {
		const res = await GD.REQ_API.request({
			httpMethod: "POST",
			method: "cardholders/get",
			data: { uid: "-1" },
		});

		if (res.error) return res;

		return { data: this.decorate(res.data as ICardHolderResponse) };
	};

	private preparePayload = (data: ICardHolderRaw) => {
		const payload: DynamicRecord = { ...data };

		// because of server error
		delete payload.providerData;

		payload.address = {
			address_line1: data.address_line1,
			address_line2: data.address_line2,
			city_code: data.city_code,
			state: data.state,
			postcode: data.postcode,
			country_code: data.country_code,
		};
		return payload;
	};

	private create: (data: ICardHolderRaw) => Promise<IAPIResponse<ICardHolderSaved>> = async data => {
		const payload = this.preparePayload(data);

		const res = await GD.REQ_API.request({
			method: "cardholders/register",
			httpMethod: "POST",
			data: payload,
		});
		if (res.error) {
			if (res.error.code === CARD_HOLDER_PHONE_TAKEN_ERROR_CODE) return { error: attachPhoneTakenField(res.error, "mobile") };
			if (res.error.details && Array.isArray(res.error.details)) {
				return { error: { code: res.error.code, message: "Validation Error", details: res.error.details } };
			}
			return { error: res.error };
		}

		if (!res.error) {
			const cardHolders = await this.getAll();
			if (cardHolders.data) GD.S_CARDHOLDERS_LIST_UPDATED.invoke(cardHolders.data);
		}

		return { data: res.data as ICardHolderSaved };
	};

	private getAll: () => Promise<IAPIResponse<ICardHolderSaved[]>> = async () => {
		return (await GD.REQ_API.request({
			httpMethod: "GET",
			method: "cardholders/list",
		})) as IAPIResponse<ICardHolderSaved[]>;
	};

	private get: (uid: string) => Promise<IAPIResponse<ICardHolderSaved>> = async uid => {
		if (!uid) return { error: { code: 1, message: "Card holder UID is required" } };

		const res = await GD.REQ_API.request({
			httpMethod: "POST",
			method: "cardholders/get",
			data: { uid },
		});
		if (res.error) return res;

		const decorated = this.decorate(res.data as ICardHolderResponse) as unknown as ICardHolderSaved;
		this._snapshots[uid] = this.stripMarkup(decorated);
		return { data: decorated };
	};

	// Plain copy of the flattened form data without `$__` meta — the baseline for diffing.
	private stripMarkup = (data: object): DynamicRecord => {
		const source = data as DynamicRecord;
		const out: DynamicRecord = {};
		for (const key in source) {
			if (key.startsWith("$__")) continue;
			out[key] = source[key];
		}
		return out;
	};

	// Build the partial-update payload: only fields that actually changed vs the
	// snapshot taken at `get` time, plus uid. The backend preserves everything else.
	private buildDelta = (data: ICardHolderSaved, snapshot: DynamicRecord) => {
		const source = data as unknown as DynamicRecord;
		const delta: DynamicRecord = { uid: data.uid };
		const address: DynamicRecord = {};

		for (const key in data) {
			if (key.startsWith("$__")) continue;
			if (CARDHOLDER_IMMUTABLE_FIELDS.includes(key)) continue;
			// Form controls can return "1" for an API value that was numeric 1.
			if (!formValuesEqual(source[key], snapshot[key])) {
				if (CARDHOLDER_ADDRESS_FIELDS.includes(key)) address[key] = source[key];
				else delta[key] = source[key];
			}
		}

		if (Object.keys(address).length > 0) delta.address = address;
		return delta;
	};

	private update: (data: ICardHolderSaved) => Promise<IAPIResponse<IStatusResponse>> = async data => {
		const snapshot = data.uid ? this._snapshots[data.uid] : undefined;
		const payload = snapshot ? this.buildDelta(data, snapshot) : this.preparePayload(data);
		const res = (await GD.REQ_API.request({
			httpMethod: "POST",
			method: "cardholders/update",
			data: payload,
		})) as IAPIResponse<IStatusResponse>;

		if (res.error) {
			if (res.error.code === CARD_HOLDER_PHONE_TAKEN_ERROR_CODE) return { error: attachPhoneTakenField(res.error, "mobile") };
			if (res.error.details && Array.isArray(res.error.details)) {
				return { error: { code: res.error.code, message: "Validation Error", details: res.error.details } };
			}
			return { error: res.error };
		}

		if (res.data?.status === true) {
			const cardHolders = await this.getAll();
			if (cardHolders.data) GD.S_CARDHOLDERS_LIST_UPDATED.invoke(cardHolders.data);
			return { data: res.data };
		}

		return res as IAPIResponse<IStatusResponse>;
	};
}
